import { useMemo } from "react";
import { useParams, Link } from "react-router";
import { useAuth } from "@/hooks/useAuth";
import { renderDocumentLabel } from "@/lib/formatters";
import { useDocumentTrail } from "../hooks/useDocumentTrail";
import {
  WorkflowTimeline,
  type StepNode,
} from "../components/WorkflowTimeline";
import {
  FieldCorrectionsTable,
} from "../components/FieldCorrectionsTable";
import "./DocumentTrailPage.css";

/**
 * Phase 18 WU-18.F3 — document drill-through at
 * `/analytics/documents/:id/trail`.
 *
 * Permission gate: caller must hold `analytics.view_own` OR
 * `analytics.view_others`. The API enforces view_own-only callers must be
 * the primaryOperator of the doc; this page only checks the permission
 * presence — 403s come back as an error banner.
 *
 * 410 Gone (detail > 90 days) is rendered as a distinct guard panel
 * instead of a generic error.
 */
export function DocumentTrailPage() {
  const { id } = useParams();
  const documentId = id ?? "";
  const { user } = useAuth();

  const trail = useDocumentTrail(documentId);

  const permissions = user?.permissions ?? [];
  const hasPermission =
    permissions.includes("analytics.view_own") ||
    permissions.includes("analytics.view_others");

  const stepNodes = useMemo<StepNode[]>(() => {
    const raw = (trail.data?.stepTrail ?? []) as unknown[];
    if (!Array.isArray(raw)) return [];
    return raw.map((entry, idx) => {
      const r = entry as Record<string, unknown>;
      const durationCandidate = r["durationMs"] ?? r["duration"] ?? 0;
      const durationMs = typeof durationCandidate === "number" ? durationCandidate : 0;
      return {
        stepId: String(r["stepId"] ?? r["id"] ?? idx),
        stepName: String(r["stepName"] ?? r["name"] ?? r["stepId"] ?? r["id"] ?? `Step ${idx + 1}`),
        durationMs,
        operatorId: (r["operatorId"] as string | undefined) ?? null,
        operatorName: (r["operatorName"] as string | undefined) ?? null,
        breach: Boolean(r["breach"]),
      };
    });
  }, [trail.data]);

  if (user && !hasPermission) {
    return (
      <div className="doc-trail">
        <header className="doc-trail__header">
          <h1 className="doc-trail__title">Document Trail</h1>
        </header>
        <div className="doc-trail__forbidden" data-testid="trail-forbidden">
          You need <strong>analytics.view_own</strong> or
          <strong> analytics.view_others</strong> permission to view trails.
        </div>
      </div>
    );
  }

  if (trail.isLoading) {
    return (
      <div className="doc-trail">
        <header className="doc-trail__header">
          <h1 className="doc-trail__title">{documentId}</h1>
        </header>
        <div className="analytics-loading" data-testid="trail-loading">Loading…</div>
      </div>
    );
  }

  if (trail.error?.status === 410) {
    return (
      <div className="doc-trail">
        <header className="doc-trail__header">
          <h1 className="doc-trail__title">{documentId}</h1>
        </header>
        <div className="doc-trail__gone" data-testid="trail-gone">
          <strong>Detail retained 90 days.</strong>
          <span>
            {" "}This document's trail has been evicted. A monthly summary is
            still available in the aggregate analytics views.
          </span>
        </div>
      </div>
    );
  }

  if (trail.error) {
    return (
      <div className="doc-trail">
        <header className="doc-trail__header">
          <h1 className="doc-trail__title">{documentId}</h1>
        </header>
        <div className="analytics-error" data-testid="trail-error">
          {trail.error.message}
        </div>
      </div>
    );
  }

  const data = trail.data;
  if (!data) return null;

  const { stats, document, fieldCorrections } = data;
  const kpm =
    stats.activeKeyingMs > 0
      ? (stats.keystrokeCount / stats.activeKeyingMs) * 60_000
      : 0;
  const slaChipClass = stats.slaMet === true
    ? "doc-trail__chip doc-trail__chip--met"
    : stats.slaMet === false
      ? "doc-trail__chip doc-trail__chip--missed"
      : "doc-trail__chip doc-trail__chip--unknown";
  const slaChipLabel = stats.slaMet === true ? "SLA met" : stats.slaMet === false ? "SLA missed" : "SLA n/a";

  return (
    <div className="doc-trail">
      <header className="doc-trail__header">
        <Link to="/admin/analytics" className="doc-trail__back">
          ← Analytics
        </Link>
        <h1
          className="doc-trail__title"
          data-testid="doc-trail-title"
        >
          {/* R-167 WU-7 — operator-keyed customDisplayLabel wins; falls back
              to the R-132 structured displayId, then the cuid-prefix. */}
          {renderDocumentLabel(document)}
        </h1>
        <div className="doc-trail__meta">
          <span>
            Batch:{" "}
            <Link
              to={`/analytics/batches/${document.batchId}/trail`}
              className="analytics-link"
            >
              {document.batchId}
            </Link>
          </span>
          <span>Classification: {document.classification ?? "—"}</span>
          <span>Status: {document.status}</span>
          <span className={slaChipClass}>{slaChipLabel}</span>
        </div>
      </header>

      <section className="doc-trail__section">
        <h2 className="analytics-section-title">Workflow Timeline</h2>
        <div data-testid="trail-timeline">
          <WorkflowTimeline
            steps={stepNodes}
            slaTargetMs={stats.slaTargetMs}
            totalDurationMs={stats.totalDurationMs}
          />
        </div>
      </section>

      <section className="doc-trail__section" data-testid="trail-performance">
        <h2 className="analytics-section-title">Performance</h2>
        <div className="doc-trail__stats">
          <div className="analytics-stat-card">
            <div className="analytics-stat-value">{stats.keystrokeCount.toLocaleString()}</div>
            <div className="analytics-stat-label">Keystrokes</div>
          </div>
          <div className="analytics-stat-card">
            <div className="analytics-stat-value">{kpm.toFixed(1)}</div>
            <div className="analytics-stat-label">KPM</div>
          </div>
          <div className="analytics-stat-card">
            <div className="analytics-stat-value">
              {(stats.activeKeyingMs / 1000).toFixed(0)}s
            </div>
            <div className="analytics-stat-label">Active Keying</div>
          </div>
          <div className="analytics-stat-card">
            <div className="analytics-stat-value">
              {stats.idleMs != null ? `${(stats.idleMs / 1000).toFixed(0)}s` : "—"}
            </div>
            <div className="analytics-stat-label">Idle</div>
          </div>
          <div className="analytics-stat-card">
            <div className="analytics-stat-value">
              {stats.timePerFieldMs != null ? `${stats.timePerFieldMs}ms` : "—"}
            </div>
            <div className="analytics-stat-label">Time / Field</div>
          </div>
        </div>
      </section>

      <section className="doc-trail__section">
        <h2 className="analytics-section-title">Field Corrections</h2>
        <div data-testid="trail-corrections-table">
          <FieldCorrectionsTable corrections={fieldCorrections} />
        </div>
      </section>

      <section className="doc-trail__section">
        <details className="doc-trail__raw" data-testid="trail-raw-steptrail">
          <summary>Raw step trail</summary>
          <pre className="doc-trail__raw-pre">
            {JSON.stringify(data.stepTrail, null, 2)}
          </pre>
        </details>
      </section>
    </div>
  );
}
